import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { plural } from "@/utils/formatText";

import { DETAIL_LIST, PILL_NEUTRAL, TONE } from "../constants";
import Chip from "./Chip";
import ErrorNote from "./ErrorNote";

// The three answers, in the order they matter. `missing` is what breaks a
// request; `different` is what breaks it later, under a specific value; `extra`
// is usually harmless leftovers.
const GROUPS = [
    {
        key: "missing",
        title: "The code expects it and this database has not got it",
        tone: TONE.rose,
        hint: "A query touching one of these fails on this database.",
    },
    {
        key: "different",
        title: "Both have it and they disagree",
        tone: TONE.amber,
        hint:
            "The column exists but not as the code describes it. A nullable " +
            "column the code believes is NOT NULL will hold a NULL the code " +
            "never expects to read.",
    },
    {
        key: "unexpected",
        title: "This database has it and the code does not define it",
        tone: TONE.slate,
        hint:
            "Usually a leftover from a reverted feature. Harmless, but it is " +
            "also what a hand-applied change looks like.",
    },
];

/**
 * Does this database's schema actually match what the code expects?
 *
 * The revision hash above this panel is a *claim*. `alembic_version` is one
 * string in one table, and everything else on this screen takes it on trust.
 * This checks it by comparing the live catalogue against the ORM's own metadata.
 *
 * The reason it earns a place next to the revision: a database can be stamped at
 * head and still be missing a column. A half-applied migration, a hand-run
 * ALTER, an `alembic stamp` used to get past a failure, a tenant provisioned
 * from an old template — all of them leave the version looking right and the
 * schema wrong, and none of them show up anywhere else.
 *
 * Loaded on request, not on open: it reads a whole catalogue.
 */
const SchemaDiffPanel = ({ target, autoLoad = false }) => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!target) return;
        try {
            setLoading(true);
            setError(null);
            setResult(await PlatformService.fetchSchemaDiff(target));
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setLoading(false);
        }
    }, [target]);

    useEffect(() => {
        if (autoLoad) load();
    }, [autoLoad, load]);

    const grouped = (key) =>
        (result?.differences ?? []).filter((d) => d.direction === key);

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-medium text-text-secondary">
                    Does the schema match the code?
                </div>
                <Button
                    onClick={load}
                    disabled={loading}
                    className={PILL_NEUTRAL}
                    title="Compare this database's real tables and columns against what the code expects"
                >
                    <RefreshCw
                        size={12}
                        aria-hidden="true"
                        className={loading ? "animate-spin" : ""}
                    />
                    {loading ? "Checking…" : result ? "Check again" : "Check the schema"}
                </Button>
            </div>

            {!result && !loading && !error && (
                <div className="text-[11px] text-text-secondary">
                    The revision above is what this database <em>says</em> it is on.
                    This compares its real tables and columns against what the code
                    expects, which is the only way to catch a migration that was
                    stamped but never fully applied.
                </div>
            )}

            {loading && <Skeleton className="h-20 w-full" />}

            {error && <ErrorNote>{error}</ErrorNote>}

            {/* The server reached the database but could not compare it. Not the
                same as "no differences", and it must not read as it. */}
            {result?.error && (
                <Callout tone="warning" icon={AlertTriangle} size="xs">
                    <span className="font-medium block mb-0.5">
                        The schema could not be compared
                    </span>
                    {result.error}
                </Callout>
            )}

            {result && !result.error && (
                <>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-text-secondary">
                        <span>
                            {result.tablesPresent} tables here ·{" "}
                            {result.tablesExpected} defined in the code
                        </span>
                        {result.inSync ? (
                            <Chip
                                tone={TONE.emerald}
                                icon={<CheckCircle2 size={11} />}
                                label="matches the code"
                            />
                        ) : (
                            <Chip
                                tone={TONE.amber}
                                icon={<ShieldAlert size={11} />}
                                label={plural(
                                    result.differences.length,
                                    "difference",
                                    "differences"
                                )}
                            />
                        )}
                    </div>

                    {result.inSync ? (
                        <Callout tone="neutral" icon={CheckCircle2} size="xs">
                            Every table, column, index and constraint the code
                            defines is here as described. The recorded revision is
                            telling the truth.
                        </Callout>
                    ) : (
                        GROUPS.map((group) => {
                            const rows = grouped(group.key);
                            if (!rows.length) return null;
                            return (
                                <div key={group.key}>
                                    <div className="flex items-center gap-2 mb-1 mt-2">
                                        <div className="text-[11px] font-medium text-text-secondary">
                                            {group.title}
                                        </div>
                                        <Chip
                                            tone={group.tone}
                                            label={String(rows.length)}
                                            title={group.hint}
                                        />
                                    </div>
                                    <div className={DETAIL_LIST}>
                                        {rows.map((difference, index) => (
                                            <div
                                                key={`${difference.kind}-${difference.table}-${difference.target}-${index}`}
                                                className="px-3 py-1.5"
                                            >
                                                <div className="text-[11px] text-text-secondary leading-snug break-words">
                                                    {difference.summary}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </>
            )}
        </div>
    );
};

export default SchemaDiffPanel;
